import React, { useEffect, useState } from "react";
import { apiAdmin, formatCLP, BACKEND_URL } from "../../lib/api";
import { Plus, Trash2, Edit3, X, Upload } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { value: "3d", label: "3D" },
  { value: "papeleria", label: "Papelería" },
];
const CATEGORIES_3D = ["pokemon", "dragones", "articulados", "anime", "merch"];
const CATEGORIES_PAP = ["libros", "agendas", "stickers", "vinilos"];

const empty = { name: "", description: "", price: "", image_url: "", category: "pokemon", section: "3d", featured: false };

const Products = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = () => apiAdmin.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description, price: p.price ?? "", image_url: p.image_url, category: p.category, section: p.section || "3d", featured: !!p.featured });
    setShowForm(true);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiAdmin.post("/upload", fd);
      setForm((f) => ({ ...f, image_url: `${BACKEND_URL}${res.data.url}` }));
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir imagen"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    const payload = { ...form, price: form.price === "" ? null : Number(form.price) };
    if (!payload.name || !payload.image_url) { toast.error("Nombre e imagen requeridos"); return; }
    try {
      if (editing) await apiAdmin.put(`/products/${editing.id}`, payload);
      else await apiAdmin.post("/products", payload);
      toast.success("Guardado"); setShowForm(false); load();
    } catch { toast.error("Error al guardar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar producto?")) return;
    await apiAdmin.delete(`/products/${id}`); toast.success("Eliminado"); load();
  };

  const categories = form.section === "papeleria" ? CATEGORIES_PAP : CATEGORIES_3D;

  return (
    <div data-testid="admin-products">
      <div className="flex items-end justify-between mb-10">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-yolk">─ Catálogo ─</span>
          <h1 className="mt-2 font-heading text-4xl font-bold text-brand-ink">Productos</h1>
        </div>
        <button onClick={openNew} data-testid="product-new-btn" className="btn-chunky inline-flex items-center gap-2 bg-mint-deep text-white px-5 py-3 text-sm">
          <Plus className="h-4 w-4" /> Nuevo producto
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white border-2 border-black">
            <div className="aspect-[4/3] bg-mint-light">
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] uppercase tracking-[0.18em] text-mint-deep font-bold">{p.section}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-yolk font-bold">/ {p.category}</span>
              </div>
              <h3 className="font-heading font-bold text-brand-ink mt-1">{p.name}</h3>
              <p className="text-sm text-gray-600 mt-1">{formatCLP(p.price)}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} className="btn-chunky flex-1 inline-flex items-center justify-center gap-2 bg-white text-black px-3 py-2 text-xs">
                  <Edit3 className="h-3 w-3" /> Editar
                </button>
                <button onClick={() => remove(p.id)} data-testid={`product-delete-${p.id}`} className="btn-chunky inline-flex items-center justify-center px-3 py-2 bg-red-100 text-red-600">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <div className="bg-white border-2 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b-2 border-black sticky top-0 bg-white">
              <h2 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nuevo"} producto</h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-yolk-light"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <I label="Nombre" value={form.name} onChange={(v) => setForm({...form, name: v})} testid="product-form-name" />
              <T label="Descripción" value={form.description} onChange={(v) => setForm({...form, description: v})} testid="product-form-description" />
              <div className="grid grid-cols-2 gap-4">
                <I label="Precio (vacío = Cotizar)" type="number" value={form.price} onChange={(v) => setForm({...form, price: v})} testid="product-form-price" />
                <S label="Sección" value={form.section} onChange={(v) => setForm({...form, section: v, category: v === "papeleria" ? "libros" : "pokemon"})} options={SECTIONS} testid="product-form-section" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Categoría</label>
                <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="product-form-category" className="w-full border-2 border-black px-4 py-3 text-sm bg-white">
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">Imagen</label>
                <input type="text" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="URL o subir archivo" data-testid="product-form-image-url" className="w-full border-2 border-black px-4 py-3 text-sm mb-3" />
                <label className="btn-chunky inline-flex items-center gap-2 bg-yolk text-black px-4 py-2 cursor-pointer text-sm">
                  <Upload className="h-4 w-4" />
                  {uploading ? "Subiendo..." : "Subir imagen"}
                  <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="product-form-upload" />
                </label>
                {form.image_url && <img src={form.image_url} alt="preview" className="mt-3 h-24 w-24 object-cover border-2 border-black" />}
              </div>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} data-testid="product-form-featured" className="h-4 w-4" />
                <span className="text-sm">Destacado en página principal</span>
              </label>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowForm(false)} className="btn-chunky flex-1 bg-white text-black py-3 text-sm">Cancelar</button>
                <button onClick={submit} data-testid="product-form-submit" className="btn-chunky flex-1 bg-mint-deep text-white py-3 text-sm">Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const I = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full border-2 border-black px-4 py-3 text-sm focus:outline-none focus:border-mint-deep" />
  </div>
);
const T = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">{label}</label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} className="w-full border-2 border-black px-4 py-3 text-sm focus:outline-none focus:border-mint-deep resize-none" />
  </div>
);
const S = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] font-bold text-brand-ink mb-2">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full border-2 border-black px-4 py-3 text-sm bg-white">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export default Products;
